import { useEffect, useState } from 'react';
import { api } from '../api';
import type { Pack } from '../api/types';
import { PREFIXO_FRAGMENTO_PENTA, PREFIXO_ORBE_PENTA, type Penta, type PentaAltarLinha, type PentaCanal, type PentaEstado } from '../api/evento';
import { numero, t } from '../i18n/idioma';
import { CORES_PENTA, COR_HEX, TODAS_PENTA, contarOrbes, type LugarPenta } from '../world/pentagrama';
import { Gump } from './Gump';
import { ItemIcon } from './ItemIcon';

interface Props {
  lugar: LugarPenta;
  canal: PentaCanal | null;
  estado: PentaEstado | null;
  altar: readonly PentaAltarLinha[];
  eu: string;
  /** Sobe quando a mochila mudou (forja, prêmio aceito): relê as contas. */
  tick: number;
  onForjar(cor: number): void;
  onEngastar(cor: number, engastar: boolean): void;
  onRitual(): void;
  onCancelar(): void;
}

const qtdDe = (pack: Pack | null, defId: string) =>
  (pack?.items ?? []).filter(i => i.defId === defId && !i.equippedSlot).reduce((s, i) => s + i.qty, 0);

/**
 * O gump do Santuário do Pentagrama (techspec-pentagrama §05, §06): perto de uma ponta, forjar a orbe da cor e
 * engastar a que já tem; no centro, o Ritual das Cinco Orbes. Durante a forja ou o ritual, a barra do canal. O que
 * aparece aqui é só a conta do cliente: o servidor confere tudo de novo e avisa o motivo quando recusa.
 */
export function PentaGump({ lugar, canal, estado, altar, eu, tick, onForjar, onEngastar, onRitual, onCancelar }: Props) {
  const [regras, setRegras] = useState<Penta | null>(null);
  const [pack, setPack] = useState<Pack | null>(null);
  const [agora, setAgora] = useState(Date.now());

  useEffect(() => {
    let vivo = true;
    void api.eventoPentagrama().then(r => { if (vivo) setRegras(r); }).catch(() => {});
    return () => { vivo = false; };
  }, [lugar?.tipo, tick]);
  useEffect(() => {
    let vivo = true;
    void api.getPack().then(p => { if (vivo) setPack(p); }).catch(() => {});
    return () => { vivo = false; };
  }, [lugar?.tipo, lugar && lugar.tipo === 'ponta' ? lugar.cor : -1, tick, canal === null]);
  useEffect(() => {
    if (!canal) return;
    const id = window.setInterval(() => setAgora(Date.now()), 100);
    return () => window.clearInterval(id);
  }, [canal]);

  if (!lugar && !canal) return null;
  const r = regras?.regras;
  const prata = (pack?.silver ?? 0) + (pack?.prataBanco ?? 0);
  const meu = altar.find(l => l.nome === eu)?.mascara ?? 0;

  if (canal) {
    const resta = Math.max(0, canal.ateMs - agora);
    const pct = canal.ms > 0 ? Math.min(100, 100 - (resta / canal.ms) * 100) : 100;
    const cor = canal.tipo === 1 ? CORES_PENTA[canal.cor] : null;
    return (
      <div className="penta-gump">
        <Gump title={canal.tipo === 1 && cor ? t('penta.forjando', { orbe: t(`penta.orbe.${cor}`) }) : t('penta.ritualAndamento')}>
          <div className="penta-canal">
            <span className="penta-canal__bar" style={{ ['--cor' as string]: cor ? COR_HEX[cor] : '#c9a8ff' }}>
              <i style={{ width: `${pct}%` }} />
            </span>
            <div className="penta-canal__l">
              <small>{t('penta.fiqueParado')}</small>
              <b className="ev-mono">{(resta / 1000).toFixed(1)} s</b>
            </div>
            <button type="button" className="btn btn--mini" onClick={onCancelar}>{t('penta.cancelar')}</button>
          </div>
        </Gump>
      </div>
    );
  }

  if (lugar?.tipo === 'ponta') {
    const cor = CORES_PENTA[lugar.cor];
    const frag = qtdDe(pack, PREFIXO_FRAGMENTO_PENTA + cor);
    const orbes = qtdDe(pack, PREFIXO_ORBE_PENTA + cor);
    const precisa = r?.fragmentosPorOrbe ?? 6;
    const custo = r?.prataPorOrbe ?? 0;
    const pode = frag >= precisa && prata >= custo;
    const desperta = estado?.cor === lugar.cor;
    const engastada = (meu & (1 << lugar.cor)) !== 0;
    return (
      <div className="penta-gump">
        <Gump title={t(`penta.ponta.${cor}`)}>
          <div className="penta-ponta" style={{ ['--cor' as string]: COR_HEX[cor] }}>
            {desperta && <p className="penta-desperta">{t('penta.pontaDesperta')}</p>}
            <div className="penta-linha">
              <ItemIcon id={PREFIXO_ORBE_PENTA + cor} size={34} label={t(`penta.orbe.${cor}`)} />
              <div>
                <b>{t(`penta.orbe.${cor}`)}</b>
                <small>
                  <span className={frag >= precisa ? 'ok' : 'nok'}>{t('penta.fragmentosDe', { n: frag, de: precisa })}</span>
                  {' · '}
                  <span className={prata >= custo ? 'ok' : 'nok'}>{t('penta.prataDe', { n: numero(custo) })}</span>
                </small>
              </div>
            </div>
            <button type="button" className="btn btn--primary ev-cheio" disabled={!pode} onClick={() => onForjar(lugar.cor)}>
              {t('penta.forjar', { s: r?.forjaSegundos ?? 8 })}
            </button>
            {(orbes > 0 || engastada) && (
              <button type="button" className="btn ev-cheio" onClick={() => onEngastar(lugar.cor, !engastada)}>
                {engastada ? t('penta.recolher') : t('penta.engastar')}
              </button>
            )}
          </div>
        </Gump>
      </div>
    );
  }

  const n = contarOrbes(meu);
  const custo = r?.prataRitual ?? 0;
  const temRobe = regras?.temRobe ?? false;
  return (
    <div className="penta-gump">
      <Gump title={t('penta.ritual')}>
        <div className="penta-ponta">
          <div className="penta-pips" aria-label={t('penta.engastadas', { n })}>
            {CORES_PENTA.map((c, i) => (
              <i key={c} className={meu & (1 << i) ? 'is-on' : ''} style={{ ['--cor' as string]: COR_HEX[c] }} title={t(`penta.orbe.${c}`)} />
            ))}
          </div>
          {/* Quem já tem o robe pode repetir: o novo vai para a mochila, para trocar ou vender (Renato, 24/10). */}
          {temRobe && <p className="ev-dica">{t('penta.jaTemRobe')}</p>}
          <p className="ev-dica">
            {n < 5 ? t('penta.faltaEngastar', { n: 5 - n }) : t('penta.prontoRitual')}
            {' '}<span className={prata >= custo ? 'ok' : 'nok'}>{t('penta.prataDe', { n: numero(custo) })}</span>
          </p>
          <button type="button" className="btn btn--primary ev-cheio" disabled={meu !== TODAS_PENTA || prata < custo} onClick={onRitual}>
            {t('penta.concluir', { s: r?.ritualSegundos ?? 20 })}
          </button>
        </div>
      </Gump>
    </div>
  );
}
